import { BuildingOfficeIcon, TagIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';

import type { CarrierDivision } from '@/services/api/carriersApiService';
import { carriersApiService } from '@/services/api/carriersApiService';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';
import { CarrierLink } from './CarrierLink';

/**
 * Carrier division tooltip content component
 * Shows division name and links to the parent carrier.
 */
export const CarrierDivisionTooltipContent = memo<
  EntityTooltipContentProps<CarrierDivision>
>(({ fetchState }) => {
  if (fetchState.status === 'pending') {
    return <LoadingState rows={1} />;
  }

  if (fetchState.status === 'error') {
    return (
      <ErrorState error={fetchState.error} entityType="carrier division" />
    );
  }

  if (fetchState.status === 'idle') {
    return <EmptyState entityType="carrier division" />;
  }

  const division = fetchState.data;

  return (
    <div className="divide-y divide-gray-100">
      <TooltipHeader
        icon={BuildingOfficeIcon}
        title={division.divisionName || 'Division'}
      />

      <TooltipSection withBorder>
        <InfoRow
          icon={TagIcon}
          label="Division"
          value={division.divisionName}
        />
        <InfoRow
          icon={BuildingOfficeIcon}
          label="Carrier"
          value={
            <CarrierLink
              entityId={division.carrierId}
              side="left"
              className="text-xs"
            >
              View Carrier
            </CarrierLink>
          }
          disableCopy
        />
      </TooltipSection>
    </div>
  );
});

CarrierDivisionTooltipContent.displayName = 'CarrierDivisionTooltipContent';

/**
 * Hoverable carrier division link with tooltip preview
 *
 * Shows division name and parent carrier on hover.
 * Used in sync suggestion cards to display carrier directory references.
 *
 * @example
 * ```tsx
 * <CarrierDivisionLink entityId={divisionId}>
 *   Carrier Directory
 * </CarrierDivisionLink>
 * ```
 */
export const CarrierDivisionLink = createEntityLink<
  CarrierDivision,
  { divisionId: string }
>({
  tooltipContent: CarrierDivisionTooltipContent,
  getDetailUrl: null,
  useQuery:
    carriersApiService.endpoints.getCarrierDivision.useQuery,
  getQueryArg: (id) => ({ divisionId: id }),
  displayName: 'CarrierDivisionLink',
});
